import { ActionIcon, Loader, Textarea } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconMicrophone,
  IconPlayerStop,
  IconSend,
  IconX,
} from "@tabler/icons-react";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { ASSISTANT_NAME } from "utils/assistantIdentity";
import css from "./MicChatDrawer.module.scss";
import useMicRecording from "./useMicRecording";

const ChatComposer = forwardRef(({ disabled, onSendMessage }, ref) => {
  const [value, setValue] = useState("");
  const inputRef = useRef(null);
  const trimmedValue = value.trim();

  const showMicError = (message) => {
    showNotification({
      title: "Voice input unavailable",
      message,
      color: "red",
    });
  };

  const handleTranscript = (transcript) => {
    const text = typeof transcript === "string" ? transcript.trim() : "";
    if (!text) return;

    setValue((current) => {
      const trimmedCurrent = current.trimEnd();
      return trimmedCurrent ? `${trimmedCurrent} ${text}` : text;
    });
    inputRef.current?.focus();
  };

  const {
    micState,
    startListening,
    stopListening,
    cancelListening,
    isSupported: isMicSupported,
    waveformCanvasRef,
  } = useMicRecording({
    onTranscript: handleTranscript,
    onError: showMicError,
  });

  const isListening = micState === "listening";
  const isTranscribing = micState === "transcribing";
  const isRecordingFlow = isListening || isTranscribing;
  const sendDisabled = disabled || isRecordingFlow || !trimmedValue;

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  const sendMessage = () => {
    if (sendDisabled) {
      return;
    }

    onSendMessage(trimmedValue);
    setValue("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <form className={css.composer} onSubmit={handleSubmit}>
      {isListening && (
        <div className={css.composerWaveform}>
          <canvas
            ref={waveformCanvasRef}
            aria-hidden="true"
            className={css.composerWaveformCanvas}
          />
        </div>
      )}

      {isTranscribing && (
        <div className={css.composerTranscribing}>
          <Loader size="xs" color="#1e88d1" />
          <span>Transcribing…</span>
        </div>
      )}

      {!isRecordingFlow && (
        <Textarea
          ref={inputRef}
          value={value}
          onChange={(event) => setValue(event.currentTarget.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Ask ${ASSISTANT_NAME}`}
          autosize
          minRows={1}
          maxRows={5}
          classNames={{
            root: css.composerInputRoot,
            input: css.composerInput,
          }}
          aria-label={`Message ${ASSISTANT_NAME}`}
        />
      )}

      {isListening ? (
        <>
          <ActionIcon
            radius="xl"
            size={42}
            onClick={() => stopListening()}
            className={css.composerStop}
            aria-label="Stop recording"
          >
            <IconPlayerStop size={18} stroke={2} />
          </ActionIcon>
          <ActionIcon
            radius="xl"
            size={42}
            onClick={cancelListening}
            className={css.composerCancel}
            aria-label="Cancel recording"
          >
            <IconX size={18} stroke={2} />
          </ActionIcon>
        </>
      ) : (
        <>
          {isMicSupported && (
            <ActionIcon
              radius="xl"
              size={42}
              onClick={startListening}
              disabled={disabled || isTranscribing}
              className={css.micButton}
              aria-label="Record voice message"
            >
              <IconMicrophone size={18} stroke={2} />
            </ActionIcon>
          )}
          <ActionIcon
            type="submit"
            radius="xl"
            size={42}
            disabled={sendDisabled}
            className={css.sendButton}
            aria-label="Send message"
          >
            <IconSend size={18} stroke={2} />
          </ActionIcon>
        </>
      )}
    </form>
  );
});

ChatComposer.displayName = "ChatComposer";

export default ChatComposer;
